<template>
    <div>
        <kgr-lightbox
            v-model="showLightbox"
            :headline="$t('Mitgliedsnummer manuell vergeben')"
            animated
            @post-input="closeAndAbort()"
        >
            <kgr-loading
                v-if="pending"
                inline
            />

            <div v-else>
                <div
                    class="kgr-row kgr-text--leftAligned dialog"
                    v-html="$t('Hier kannst du die automatisch vergebene Mitgliedsnummer überschreiben.')"
                ></div>

                <div class="kgr-row">
                    <kgr-form-row
                        :label="$t('Prefix')"
                        inline
                        class="fix"
                        :error="emptyError"
                        field-name="membershipNumberPrefix"
                    >
                        <input
                            v-model="previewNumber.prefix"
                            type="text"
                            maxlength="6"
                            class="kgr-input kgr-input--inline"
                            @keyup="validate()"
                            @change="validate()"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :label="$t('Zähler')"
                        inline
                        field-name="membershipNumberCounter"
                        :error="emptyError"
                    >
                        <input
                            v-model="previewNumber.counter"
                            type="number"
                            min="1"
                            max="999999999"
                            class="kgr-input kgr-input--inline"
                            @keyup="validate()"
                            @change="validate()"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :label="$t('Suffix')"
                        inline
                        class="fix"
                        :error="emptyError"
                        field-name="membershipNumberSuffix"
                    >
                        <input
                            v-model="previewNumber.suffix"
                            type="text"
                            maxlength="6"
                            class="kgr-input kgr-input--inline"
                            @keyup="validate()"
                            @change="validate()"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        v-if="error.forField('membershipNumberPrefix')"
                        :error="error"
                        field-name="membershipNumberPrefix"
                    />
                    <kgr-form-row
                        v-if="error.forField('membershipNumberCounter')"
                        :error="error"
                        field-name="membershipNumberCounter"
                    />
                    <kgr-form-row
                        v-if="error.forField('membershipNumberSuffix')"
                        :error="error"
                        field-name="membershipNumberSuffix"
                    />
                </div>

                <kgr-membership-number-preview :number="previewNumber" />

                <div class="kgr-row">
                    <kgr-button
                        value="Ok"
                        :disabled="!isValid"
                        @click="onOkButtonClick"
                    />
                    <kgr-button
                        type="grey"
                        :value="$t('cancel')"
                        @click="closeAndAbort"
                    />
                </div>
            </div>
        </kgr-lightbox>
    </div>
</template>

<script>
import Error from '../../../js/model/Error';
import FormRow from '../../common/form/FormRow';
import KgrButton from '../../common/form/button/KgrButton';
import Lightbox from '../../common/modal/Lightbox';
import Loading from '../../common/Loading';
import MembershipNumber from '../../../js/dto/MembershipNumber';
import MembershipNumberPreview from '../../members/membership-number/MembershipNumberPreview';
import MembershipNumberRepository from '../../../js/repository/MembershipNumberRepository';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-lightbox': Lightbox,
        'kgr-loading': Loading,
        'kgr-form-row': FormRow,
        'kgr-membership-number-preview': MembershipNumberPreview,
    },
    emits: ['input', 'close'],
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        value: {
            type: MembershipNumber,
            required: true,
        },
    },
    data() {
        return {
            showLightbox: this.show,
            pending: false,
            error: new Error(),
            emptyError: new Error(),
            isValid: true,
            checkMessage: null,
            previewNumber: this.createPreviewNumber(),
        }
    },
    watch: {
        show() {
            this.showLightbox = this.show;
            if (this.show) {
                this.loadCurrentDigits();
            }
        },
    },
    methods: {
        loadCurrentDigits() {
            // Always refetch on open, not only when no number is assigned yet: the club's digits
            // setting may have changed since this member's number was generated, and previewNumber
            // must reflect the CURRENT setting - not whatever digits happened to be baked into the
            // already-assigned number - or the counter/digits validation below silently uses a stale
            // digit width (KIGAROO-00000).
            this.pending = true;
            MembershipNumberRepository.getNext(this.clubId)
                .then(number => {
                    if (this.value.counter === null) {
                        this.previewNumber = number;
                    } else {
                        this.previewNumber.digits = number.digits;
                    }

                    this.pending = false;
                })
                .catch(() => {
                    // Don't leave the dialog stuck on the loading spinner - with no way to even
                    // close it, since the Cancel button only renders once pending is false - if the
                    // fetch fails. This now runs on every open, not just for a brand-new number.
                    this.pending = false;
                });
        },
        pad(str, max) {
            str = str.toString();
            return str.length < max ? this.pad('0' + str, max) : str;
        },
        onOkButtonClick() {
            if (this.validate()) {
                this.showLightbox = false;
                const updated = this.previewNumber.clone();
                updated.counter = Number(updated.counter);
                updated.generated = false;
                this.$emit('input', updated);
                this.$emit('close');
            }
        },
        closeAndAbort() {
            this.showLightbox = false;
            this.error = new Error();
            this.$emit('close');
        },
        validate() {
            let regexCounter = RegExp(/^\d+$/);
            let regexPrefix = RegExp(/\d/);
            let regexSuffix = RegExp(/^\d+/);
            const counterValid = regexCounter.test(this.previewNumber.counter);
            const prefixValid = !regexPrefix.test(this.previewNumber.prefix);
            const suffixValid = !regexSuffix.test(this.previewNumber.suffix);
            this.error.clear('membershipNumberPrefix')
            this.error.clear('membershipNumberCounter')
            this.error.clear('membershipNumberSuffix')

            if (!prefixValid) {
                this.error.add('membershipNumberPrefix', this.$t('Der Prefix darf keine Zahl enthalten.'));
            }
            if (!counterValid) {
                this.error.add('membershipNumberCounter', this.$t('Die Mitgliedsnummer darf nur Zahlen enthalten.'));
            }
            if (!suffixValid) {
                this.error.add('membershipNumberSuffix', this.$t('Der Suffix darf nicht mit einer Zahl beginnen.'));
            }
            this.$forceUpdate();
            this.isValid = counterValid && prefixValid && suffixValid;

            return this.isValid;
        },
        createPreviewNumber() {
            let number = new MembershipNumber();
            number.counter = this.value.counter;
            number.prefix = this.value.prefix;
            number.suffix = this.value.suffix;
            number.digits = this.value.digits;
            number.generated = this.value.generated;

            return number;
        }
    }
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .dialog {
        width: 350px;
    }
    .kgr-input {
        background-color: var(--kgr-input-background-color) !important;
        border-style: solid !important;
        border-radius: var(--kgr-input-border-radius) !important;
        border-width: var(--kgr-input-border-width) !important;
        border-color: var(--kgr-input-border-color) !important;
    }
    .fix {
        width: 50px;
    }
    .fix .kgr-input {
        width: 50px;
    }
</style>
